/**
 * AI Notes Socket Context
 * 
 * Provides a single global Socket.IO connection for the AI Notes module
 * Prevents disconnects when switching between tabs
 * Disables socket connection on public routes (pricing, FAQ, card views, etc.)
 * Only connects when user is authenticated and on protected routes
 */

import { createContext, useContext, ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAINotesSocket } from '@/hooks/ai-notes/useAINotesSocket';
import { toast } from 'sonner';
import type { UseAINotesSocketReturn } from '@/hooks/ai-notes/useAINotesSocket';

const AINotesSocketContext = createContext<UseAINotesSocketReturn | null>(null);

interface AINotesSocketProviderProps {
  children: ReactNode;
}

export function AINotesSocketProvider({ children }: AINotesSocketProviderProps) {
  const location = useLocation();
  const { user } = useAuth();
  const lastErrorLoggedRef = useRef<string>('');
  const lastDisconnectReasonRef = useRef<string>('');
  const hasShownToastRef = useRef<boolean>(false);
  const shouldConnectRef = useRef<boolean>(false);
  
  // List of public routes that don't need WebSocket connection
  const publicRoutes = [
    '/c/',           // Card view routes
    '/card/',        // Alternative card routes
    '/pricing',      // Pricing page
    '/faq',          // FAQ page
    '/how-to',       // How-to guides
    '/about',        // About page
    '/use-cases',    // Use cases page
    '/auth',         // Auth page
    '/verify-email', // Email verification
    '/corporate-auth', // Corporate auth
    '/privacy',      // Privacy policy
    '/terms',        // Terms of service
    '/cookie-policy', // Cookie policy
  ];
  
  // Check if current route is a public route
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));
  const isAuthenticated = !!user;
  
  // Single global socket connection - don't auto-connect, we'll manage it manually
  const socket = useAINotesSocket({
    autoConnect: false,
    onConnected: () => {
      lastErrorLoggedRef.current = '';
      hasShownToastRef.current = false;
    },
    onDisconnected: () => {
      // Silent disconnect
    },
    onError: (error) => {
      const errorMsg = error.message;
      
      // Only notify once per unique error
      if (lastErrorLoggedRef.current !== errorMsg) {
        lastErrorLoggedRef.current = errorMsg;
        
        // Only show error toast if user is authenticated and haven't shown it yet
        if (isAuthenticated && !hasShownToastRef.current) {
          toast.error(`Connection error: ${error.message}`);
          hasShownToastRef.current = true;
        }
      }
    },
  });

  // Reset error tracking when authentication status changes
  useEffect(() => {
    lastErrorLoggedRef.current = '';
    lastDisconnectReasonRef.current = '';
    hasShownToastRef.current = false;
  }, [isAuthenticated]);

  // Manage connection based on route changes and authentication status
  useEffect(() => {
    // Only connect if user is authenticated AND not on public route
    const shouldConnect = isAuthenticated && !isPublicRoute;
    
    // Track if connection state actually changed
    const connectionStateChanged = shouldConnectRef.current !== shouldConnect;
    shouldConnectRef.current = shouldConnect;
    
    if (shouldConnect) {
      // Only connect if not already connected and state actually changed
      if (!socket.isConnected && connectionStateChanged) {
        socket.connect();
      }
    } else {
      // Only disconnect if connected and state actually changed
      if (socket.isConnected && connectionStateChanged) {
        socket.disconnect();
      }
    }
  }, [location.pathname, isPublicRoute, isAuthenticated, socket.isConnected]);

  return (
    <AINotesSocketContext.Provider value={socket}>
      {children}
    </AINotesSocketContext.Provider>
  );
}

/**
 * Hook to access the global AI Notes socket
 */
export function useAINotesSocketContext() {
  const context = useContext(AINotesSocketContext);
  
  if (!context) {
    throw new Error('useAINotesSocketContext must be used within AINotesSocketProvider');
  }
  
  return context;
}
